import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const InterestRateModal = ({ isOpen, onClose, onSubmit, card, loading, loanType }) => {
    const [formData, setFormData] = useState({
        icon: '🏠',
        title: '',
        description: '',
        display_order: 0,
        is_active: true,
        loan_type: loanType
    });

    useEffect(() => {
        if (card) {
            setFormData({
                ...card,
                is_active: card.is_active === 1 || card.is_active === true
            });
        } else {
            setFormData({
                icon: '🏠',
                title: '',
                description: '',
                display_order: 0,
                is_active: true,
                loan_type: loanType
            });
        }
    }, [card, loanType]);

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
    };

    const commonEmojis = ['🏠', '🔍', '🤝', '💰', '📊', '✅', '🎯', '⚡', '🌟', '💡', '🔒', '📈'];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
                <div className="flex items-center justify-between px-8 py-6 border-b border-slate-100 flex-shrink-0">
                    <h2 className="text-2xl font-bold text-slate-800">
                        {card ? 'Edit Card' : 'Add New Card'}
                    </h2>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                        <XMarkIcon className="h-6 w-6 text-slate-500" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Icon Selector */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Icon
                            </label>
                            <div className="grid grid-cols-6 gap-2">
                                {commonEmojis.map((emoji) => (
                                    <button
                                        key={emoji}
                                        type="button"
                                        onClick={() => setFormData({ ...formData, icon: emoji })}
                                        className={`text-3xl p-3 rounded-xl border-2 transition-all ${formData.icon === emoji
                                            ? 'border-primary-500 bg-primary-50'
                                            : 'border-slate-200 hover:border-slate-300'
                                            }`}
                                    >
                                        {emoji}
                                    </button>
                                ))}
                            </div>
                            <input
                                type="text"
                                value={formData.icon}
                                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                                className="mt-2 w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                                placeholder="Or enter custom emoji"
                            />
                        </div>

                        {/* Title */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Title
                            </label>
                            <input
                                type="text"
                                required
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                                placeholder="e.g., Flexible Rates"
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Description
                            </label>
                            <textarea
                                required
                                rows="3"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                                placeholder="Enter description..."
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-6">
                            {/* Display Order */}
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    Display Order
                                </label>
                                <input
                                    type="number"
                                    value={formData.display_order}
                                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                                />
                            </div>

                            {/* Active Status */}
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    Status
                                </label>
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                                    className={`w-full flex items-center justify-between px-6 py-3 rounded-2xl border transition-all ${formData.is_active
                                        ? 'bg-green-50 border-green-200 text-green-700'
                                        : 'bg-slate-50 border-slate-200 text-slate-500'
                                        }`}
                                >
                                    <span className="font-semibold">{formData.is_active ? 'Active' : 'Inactive'}</span>
                                    <div className={`w-10 h-6 rounded-full relative transition-all ${formData.is_active ? 'bg-green-500' : 'bg-slate-300'}`}>
                                        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${formData.is_active ? 'left-5' : 'left-1'}`}></div>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </form>
                </div>

                <div className="px-8 py-6 bg-slate-50 border-t border-slate-100 flex justify-end gap-3 flex-shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-3 rounded-xl font-semibold text-slate-600 hover:bg-slate-200 transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-lg"
                    >
                        {loading ? 'Saving...' : card ? 'Update Card' : 'Create Card'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InterestRateModal;
